import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  issueCommandThunk,
  restartUavConnection,
  setHoverUav,
  setSelectedUav,
} from "../features/aircraft/aircraftSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectSelectedUavId,
  selectAircraftConnectionById,
  selectNotificationsById,
  selectMissionId,
  selectFlightPhaseById,
  selectAircraftVitalsById,
  selectLandingPadById,
  selectOtherAircraft,
} from "../features/aircraft/aircraftSelectors";
import { AircraftNub } from "./AircraftNub";
import { CommandTray } from "./CommandTray";
import { NotificationTracker } from "./NotificationTracker";
import { ReactComponent as Close } from "../assets/svg/Back Arrow.svg";
import { WarningTelemetryList } from "./WarningTelemetryList";
import { ReviewFlight } from "./ReviewFlight";
import { SlideToConfirm } from "./SlideToConfirm";
import SegmentedBar from "./SegmentedBar";
import { ToggleSwitch } from "./ToggleSwitch";
import { isBoolean } from "lodash";
import { Telemetry } from "./Telemetry";
import { useSound } from "../app/soundHook";
import { VideoQuic } from "./video/VideoQuic";
import { logToDd } from "../tools/ddLog";
import { ReactComponent as TurnOffSound } from "../assets/svg/turnOffSound.svg";
import { MissionTimer } from "./MissionTimer";

import cancelIcon from "../assets/svg/Disabled.png";

const KEEP_CAMERA_ON_AFTER_DELIVERY_MS = 5000;

const actionSound = new Audio(
  require("../assets/audio/alert_high-intensity.m4a")
);
actionSound.volume = 1;
const connectionSound = new Audio(
  require("../assets/audio/navigation_selection-complete-celebration.m4a")
);
connectionSound.volume = 1;

export const AircraftData = (props: {
  uavId: string;
  uavState?: string;
  flightMode?: string;
  stale?: boolean;
  controlled?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [contextMenu, setContextMenu] =
    React.useState<{
      mouseX: number;
      mouseY: number;
    } | null>(null);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null
    );
  };
  const selectedUav = useAppSelector(selectSelectedUavId);
  const [connectedOnce, setConnectedOnce] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [toggling, setToggling] = useState<boolean>(false);
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [showTelemetry, setShowTelemetry] = useState<boolean>(false);
  const [showFts, setShowFts] = useState<boolean>(false);
  const [showExtraCommands, setShowExtraCommands] = useState<boolean>(false);
  const [soundsMuted, setSoundsMuted] = useState<boolean>(false);
  const [keepCameraAfterDelivery, setKeepCameraAfterDelivery] = useState<boolean>(false);
  const flightPhase = useAppSelector((state) =>
    selectFlightPhaseById(state, props.uavId)
  );

  const missionId = useAppSelector((state) =>
    selectMissionId(state, props.uavId)
  );
  const lastMissionId = useRef(missionId);
  const [lastMissionIdState, setLastMissionIdState] = useState<string>(
    missionId || ""
  );
  const deliveryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const vitals = useAppSelector((state) =>
    selectAircraftVitalsById(state, props.uavId)
  );
  const landingPad = useAppSelector((state) =>
    selectLandingPadById(state, props.uavId)
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClose = () => {
    setContextMenu(null);
  };
  const disconnect = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
    if (isPlaying("warning")) {
      stop("warning", props.uavId);
    }
    if (isPlaying("critical")) {
      stop("critical", props.uavId);
    }
    dispatch(restartUavConnection({ uavId: props.uavId }));
  };

  const { play, stop, isPlaying } = useSound();

  const toggleVideo = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setShowVideo((prev) => !prev);
    handleClose();
  };

  const inFlight = vitals.uavSubState?.includes("STATE_FLIGHT") ?? false;
  const inDelivery =
    (
      vitals.uavSubState?.includes("STATE_FLIGHT_MISSION_DELIVERY") ||
      vitals.uavSubState?.includes("OUTBOUND_DESCENDING") ||
      vitals.uavSubState?.includes(
        "STATE_FLIGHT_MISSION_OUTBOUND_WAITING_FOR_DELIVERY_CONFIRMATION"
      )
    ) ?? false;
  const inAbort = vitals.uavState === "ABORT";
  const inEmergency = vitals.uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
  const inPostflight = vitals.uavSubState?.includes("STATE_POSTFLIGHT") ?? false;
  const otherAircraft = useAppSelector(selectOtherAircraft);

  const prevEmergencyRef = useRef(inEmergency);
  const prevUavSubStateRef = useRef(vitals.uavSubState);
  const prevInDeliveryRef = useRef(inDelivery);

  useEffect(() => {
    const currentSubState = vitals.uavSubState;
    const prevSubState = prevUavSubStateRef.current;

    if (currentSubState && currentSubState !== prevSubState) {
      if (currentSubState.includes("STATE_PREFLIGHT") && !prevSubState?.includes("STATE_PREFLIGHT")) {
        actionSound.play();
      }

      if (currentSubState.includes("OUTBOUND_DESCENDING") &&
          !prevSubState?.includes("OUTBOUND_DESCENDING")) {
        actionSound.play();
      }

      prevUavSubStateRef.current = currentSubState;
    }
  }, [vitals.uavSubState, props.uavId]);

  useEffect(() => {
    if (inDelivery) {
      setKeepCameraAfterDelivery(true);
      if (deliveryTimeoutRef.current) {
        clearTimeout(deliveryTimeoutRef.current);
        deliveryTimeoutRef.current = null;
      }
      prevInDeliveryRef.current = inDelivery;
      return;
    }
    
    if (prevInDeliveryRef.current && !inDelivery) {
      deliveryTimeoutRef.current = setTimeout(() => {
        setKeepCameraAfterDelivery(false);
      }, KEEP_CAMERA_ON_AFTER_DELIVERY_MS);
    }
    
    prevInDeliveryRef.current = inDelivery;

    return () => {
      if (deliveryTimeoutRef.current) {
        clearTimeout(deliveryTimeoutRef.current);
        deliveryTimeoutRef.current = null;
      }
    };
  }, [inDelivery]);

  useEffect(() => {
    if (inEmergency && !prevEmergencyRef.current) {
      setSoundsMuted(false);
    }
    prevEmergencyRef.current = inEmergency;
  }, [inEmergency]);

  useEffect(() => {
    if (inAbort && !soundsMuted && !inEmergency) {
      play("warning", props.uavId);
    } else if (!inEmergency) {
      if (isPlaying("warning")) {
        stop("warning", props.uavId);
      }
    }
  }, [inAbort, soundsMuted, inEmergency, play, stop, isPlaying, props.uavId]);

  useEffect(() => {
    if (inEmergency && !soundsMuted) {
      if (isPlaying("warning")) {
        stop("warning", props.uavId);
      }
      play("critical", props.uavId);
    } else if (!inEmergency) {
      if (isPlaying("critical")) {
        stop("critical", props.uavId);
      }
    }
  }, [inEmergency, soundsMuted, play, stop, isPlaying, props.uavId]);

  useEffect(() => {
    if (!inFlight) {
      if (isPlaying("warning")) {
        stop("warning", props.uavId);
      }
      if (isPlaying("critical")) {
        stop("critical", props.uavId);
      }
      setSoundsMuted(false);
    }
  }, [inFlight, isPlaying, stop, props.uavId]);

  const handleSilenceAlarm = () => {
    setSoundsMuted(true);
    isPlaying("warning") && stop("warning", props.uavId);
    isPlaying("critical") && stop("critical", props.uavId);
  };

  const shouldShowVideo = useMemo(() => {
    return showVideo || inDelivery || inEmergency || keepCameraAfterDelivery;
  }, [showVideo, inDelivery, inEmergency, keepCameraAfterDelivery]);

  useEffect(() => {
    if (
      lastMissionId.current &&
      lastMissionIdState !== lastMissionId.current &&
      flightPhase === "missionComplete"
    ) {
      setLastMissionIdState(lastMissionId.current);
    }
    if (missionId && lastMissionId.current !== missionId) {
      lastMissionId.current = missionId;
      setShowTelemetry(false);
      logToDd(
        `${props.uavId} received mission ${missionId}`,
        `AircraftData`,
        {
          missionId: missionId,
        },
        props.uavId
      );
    }
  }, [missionId, flightPhase]);

  useEffect(() => {
    if (props.uavState) {
      setConnectedOnce(true);
    }
  }, [props.uavState]);

  const toggleShowExtra = () => {
    setShowExtraCommands(!showExtraCommands);
  };
  const showLastKnownTelemetry = () => {
    setShowTelemetry((prev) => !prev);
    handleClose();
  };

  const notifications = useAppSelector((state) =>
    selectNotificationsById(state, props.uavId)
  );
  const connection = useAppSelector((state) =>
    selectAircraftConnectionById(state, props.uavId)
  );

  useEffect(() => {
    if (connection?.connecting === false) {
      if (isPlaying("warning")) {
        stop("warning", props.uavId);
      }
      if (isPlaying("critical")) {
        stop("critical", props.uavId);
      }
      connectionSound.play();
    }
  }, [connection?.connecting, isPlaying, stop, props.uavId]);

  const issueCancelCommand = async () => {
    try {
      setLoading(true);
      await dispatch(
        issueCommandThunk({ uavId: props.uavId, command: "COMMAND_CANCEL" })
      ).unwrap();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.log(err);
    }
  };

  const selected = selectedUav === props.uavId;
  const setSelected = () => {
    if (selected) {
      dispatch(setSelectedUav(""));
    } else {
      dispatch(setSelectedUav(props.uavId));
    }
  };
  const setHover = () => {
    dispatch(setHoverUav(props.uavId));
  };
  const setNoHover = () => {
    dispatch(setHoverUav(""));
  };

  const getPhaseText = (): string => {
    if (inAbort) return "ABORT";

    if (flightPhase === "takeoff") return "Taking-off";
    if (flightPhase === "flight") return "Flying";
    if (flightPhase === "dropping") return "Delivering";
    if (flightPhase === "openingDoors") return "Opening doors";
    if (flightPhase === "climbDelivery") return "Climbing from delivery";
    if (flightPhase === "returning") return "Returning to base";
    if (flightPhase === "missionComplete") return props.uavState || "";
    if (flightPhase === "atDropLocation") return "Descending";
    if (flightPhase === "missionReceived")
      return props.uavState === "MOTORSTEST" ? `${props.uavState}` : `${props.uavState} ∙ Received`;
    if (flightPhase === "standby") return props.uavState || "";
    if (flightPhase === "ready") return props.uavState || "";
    if (flightPhase === "maintenance") return props.uavState || "";
    if (flightPhase === "pendingDelivery") return "Awaiting Command";
    return "Connecting...";
  };

  const getBorderStyle = () => {
    if (
      flightPhase === "missionReceived" ||
      flightPhase === "pendingDelivery"
    ) {
      return {
        border: 8,
        borderColor: "#00C2FF",
        animation: "actionPulse 0.5s infinite alternate",
        "@keyframes actionPulse": {
          "0%": {
            borderColor: "#D9DBE9",
          },
          "100%": {
            borderColor: "#00C2FF",
          },
        },
      };
    } else {
      return {
        border: 1,
        borderColor: "#D9DBE9",
      };
    }
  };

  const toggleAutoArm = async () => {
    try {
      setToggling(true);
      await dispatch(
        issueCommandThunk({ uavId: props.uavId, command: "COMMAND_AUTO_ARM" })
      ).unwrap();
      setToggling(false);
    } catch (err) {
      setToggling(false);
      console.log(err);
    }
  };
  const toggleFts = async () => {
    setShowFts(!showFts);
    setShowTelemetry(true);
    setShowVideo(true);
    handleClose();
  };
  const cancelFts = async () => {
    setShowFts(false);
  };

  const isCountingDown =
    flightPhase === "pendingDelivery" &&
    (vitals.uavSubState?.includes(
      "STATE_FLIGHT_MISSION_OUTBOUND_DESCENDING_TO_DELIVERY_WAIT"
    ) ||
      vitals.uavSubState?.includes(
        "STATE_FLIGHT_MISSION_OUTBOUND_WAITING_FOR_DELIVERY_CONFIRMATION"
      ));

  const renderPhaseCommand = () => {
    if (flightPhase === "missionReceived") {
      return (
        <SlideToConfirm
          style={{ height: 56 }}
          onConfirm={issueCancelCommand}
          loading={loading}
          icon={cancelIcon}
        >
          <Typography
            sx={{
              width: "100%",
              textJustify: "center",
              textAlign: "center",
            }}
            variant={"body2"}
          >
            Cancel
          </Typography>
        </SlideToConfirm>
      );
    }

    return (
      <CommandTray
        uavId={props.uavId}
        flightMode={props.flightMode}
        uavState={props.uavState}
        flightPhase={flightPhase}
        showFts={showFts}
        cancelFts={cancelFts}
        isCountingDown={isCountingDown}
      />
    );
  };

  const isAnyEmergency =
    inEmergency ||
    otherAircraft?.some((aircraft) =>
      aircraft?.uavSubState?.includes("STATE_FLIGHT_EOPS")
    );

  const shouldPulseCard = isCountingDown && !isAnyEmergency;

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      onMouseEnter={setHover}
      onMouseLeave={setNoHover}
      onContextMenu={handleContextMenu}
      bgcolor={shouldPulseCard ? "inherit" : "background.paper"}
      onClick={setSelected}
      sx={{
        userSelect: "none",
        borderRadius: 4,
        ...getBorderStyle(),
        pointerEvents: "all",
        boxSizing: "border-box",
        "&:hover": {
          boxSizing: "border-box",
          ...getBorderStyle(),
          ...(shouldPulseCard && {
            animation: "cardPulse 2s infinite ease-in-out",
          }),
        },
        ...(shouldPulseCard && {
          animation: "cardPulse 2s infinite ease-in-out",
        }),
        "@keyframes cardPulse": {
          "0%": { backgroundColor: "#101828" },
          "50%": { backgroundColor: "#005f80" },
          "100%": { backgroundColor: "#101828" },
        },
      }}
      p={2}
    >
      {flightPhase &&
      ["missionReceived", "takeoff", "flight", "pendingDelivery", "atDropLocation", "dropping", "openingDoors", "climbDelivery", "returning"].includes(
        flightPhase
      ) ? (
        <Box mb={2}>
          <SegmentedBar
            zero={flightPhase === "missionReceived"}
            one={flightPhase === "takeoff"}
            two={flightPhase === "flight"}
            three={[
              "dropping",
              "openingDoors",
              "atDropLocation",
              "pendingDelivery"
            ].includes(flightPhase)}
            four={flightPhase === "returning" || flightPhase === "climbDelivery"}
          />
        </Box>
      ) : null}
      <Box display={"flex"} flexDirection={"row"}>
        <AircraftNub
          uavId={props.uavId}
          flightMode={props.flightMode}
          uavState={props.uavState}
          stale={props.stale}
          connecting={connection?.connecting}
          inEmergency={inEmergency}
        />
        <Box
          display={"flex"}
          flex={2}
          sx={{
            maxHeight: "40px",
            height: "40px",
          }}
          flexDirection={"column"}
          alignItems={"start"}
          pl={2}
          justifyContent="center"
        >
          <Typography
            sx={{ maxLines: 1 }}
            variant={"body2"}
            fontWeight={"600"}
            fontSize="16px"
          >
            {`\n${getPhaseText()}${
              missionId ? ` ∙ ${missionId?.slice(0, 4)}` : ""
            }${
              landingPad?.name && (vitals.uavState === "STANDBY" || vitals.uavState === "MAINTENANCE") 
                ? ` ∙ ${landingPad.name}` 
                : ""
            }`}
          </Typography>
        </Box>
        {missionId && !inPostflight ? (
          <Box display={"flex"} alignItems={"center"}>
            <MissionTimer uavId={props.uavId} />
          </Box>
        ) : (
          <Box display={"flex"} alignItems={"center"}>
            <Typography>
              {isBoolean(vitals?.autoArmStatus)
                ? vitals?.autoArmStatus
                  ? "AUTO"
                  : "AUTO OFF"
                : ""}
            </Typography>
          </Box>
        )}
      </Box>
      {connectedOnce ? (
        <>
          {showExtraCommands &&
          (props.uavState === "MAINTENANCE" || props.uavState === "STANDBY") ? (
            <>
              <Box display={"flex"} mt={2} justifyContent={"space-between"}>
                <Box display={"flex"} height={32} alignItems={"center"}>
                  <Typography
                    fontWeight={600}
                    sx={{
                      textJustify: "center",
                      textAlign: "center",
                    }}
                  >
                    Extra Commands
                  </Typography>
                </Box>
                <Box display={"flex"} height={32} alignItems={"center"}>
                  <IconButton
                    color="primary"
                    aria-label="close"
                    component="span"
                    sx={{
                      width: 32,
                      height: 32,
                    }}
                    onClick={toggleShowExtra}
                  >
                    <Close stroke="#FFFFFF" />
                  </IconButton>
                </Box>
              </Box>
              <ToggleSwitch
                on={vitals?.autoArmStatus || false}
                label={"Auto Arm"}
                onChange={toggleAutoArm}
                disabled={toggling}
              />
            </>
          ) : (
            <>
              <Collapse in={props.controlled}>
                <Box pt={1} />
                {renderPhaseCommand()}
              </Collapse>

              {notifications ? (
                <Box width={"105%"}>
                  <NotificationTracker
                    notifications={notifications}
                    uavId={props.uavId}
                  />
                </Box>
              ) : null}

              {shouldShowVideo ? (
                <Box mt={1}>
                  <VideoQuic
                    ipAddresses={connection?.sims?.map((sim) => sim?.ip) || []}
                    uavId={props.uavId}
                    blurEnabled={true}
                    personDetectionEnabled={false}
                  />
                </Box>
              ) : null}

              <>
                <ReviewFlight
                  uavId={props.uavId}
                  missionId={lastMissionIdState || ""}
                  flightPhase={flightPhase || undefined}
                />
              </>

              {showTelemetry || inEmergency ? (
                <Telemetry
                  altitude={vitals.altitude}
                  battery={vitals.battery}
                  heading={vitals.heading}
                  location={
                    vitals.lat && vitals.lon ? [vitals.lat, vitals.lon] : null
                  }
                />
              ) : null}
              <WarningTelemetryList
                connection={connection}
                stale={props.stale}
              />
              {(inEmergency || inAbort) && !soundsMuted && (
                <Box 
                  sx={{ 
                    mt: 1, 
                    display: 'flex', 
                    justifyContent: 'center',
                    width: '100%'
                  }}
                >
                  <IconButton
                    onClick={handleSilenceAlarm}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '50%',
                      padding: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.15)'
                      }
                    }}
                  >
                    <TurnOffSound
                      width={40} 
                      height={40} 
                    />
                  </IconButton>
                </Box>
              )}
            </>
          )}
      <Menu
            id="demo-positioned-menu"
            transitionDuration={0}
            aria-labelledby="demo-positioned-button"
            anchorEl={anchorEl}
            open={contextMenu !== null}
            onClose={handleClose}
            anchorReference={"anchorPosition"}
            anchorPosition={
              contextMenu !== null
                ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                : undefined
        }
        PaperProps={{ "data-command-menu": true } as any}
          >
            <MenuItem disabled>
              <Typography fontWeight={800} fontSize={12}>
                Menu {`${props.uavId}`}
              </Typography>
            </MenuItem>
            {!missionId ? null : (
              <Box pl={2}>
                <Typography
                  style={{ textDecoration: "underline" }}
                  color={"#FFFFFF"}
                >
                  Mission: {`${missionId?.slice(0, 4)}`}
                </Typography>
              </Box>
            )}

            {isBoolean(vitals?.autoArmStatus) ? (
              <MenuItem onClick={toggleAutoArm} disabled={toggling}>
                <ToggleSwitch
                  disabled={toggling}
                  on={vitals?.autoArmStatus || false}
                  label={"Auto Arm"}
                ></ToggleSwitch>
              </MenuItem>
            ) : null}

            <MenuItem onClick={toggleVideo} disabled={inDelivery || inEmergency || keepCameraAfterDelivery}>
              <ToggleSwitch on={shouldShowVideo} label={"Show Video"}></ToggleSwitch>
            </MenuItem>
            <MenuItem onClick={showLastKnownTelemetry}>
              <ToggleSwitch on={showTelemetry} label={"Last Known Telemetry"} />
            </MenuItem>
            <MenuItem
              disabled
              sx={{ height: 24 }}
            ></MenuItem>
            <MenuItem onClick={disconnect}>Disconnect</MenuItem>
            {[
                  <MenuItem
                    key={"empty-space"}
                    disabled
                    sx={{ height: 24 }}
                  ></MenuItem>,
                  <MenuItem key={"fts"} onClick={toggleFts}>
                    <ToggleSwitch
                      on={showFts}
                      label={"!!! FTS !!!"}
                      onChange={toggleFts}
                    />
                  </MenuItem>,
                ]}
          </Menu>
        </>
      ) : null}
    </Box>
  );
};
